import React, { useEffect, useState } from 'react';
import { api, formatEUR } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ChevronRight, Plus, Scissors, Trash2, Edit, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { isAdmin, isGestion } from '../lib/roles';
import CaptacionCampos, {
  CAMPOS_CAPTACION, captacionCambiada, captacionInicial, cuerpoCaptacion,
} from '../components/catalogo/CaptacionCampos';
import {
  ClinicasOtroCirujanoEditor, ClinicasQuirofanoEditor, FiltroRegistroQxEditor,
  LugaresQuirofanoEditor, ProductosMapaFacialEditor,
} from '../components/catalogo/ListasCatalogo';
import PreciosCatalogo from '../components/catalogo/PreciosCatalogo';
import MarcaDocumentosMedicos from '../components/catalogo/MarcaDocumentosMedicos';

function SurgeryDialog({ open, onOpenChange, initial, onSaved, templates, admin = false }) {
  const isEdit = !!initial?.id;
  // «Captación por origen» (V2): solo admin, por su propia puerta.
  const [captacion, setCaptacion] = useState(() => captacionInicial(initial));
  const [form, setForm] = useState(() => initial || {
    code: '', name: '', description: '', price: 0, price_min: '', price_max: '', anesthesia: 'Local + sedación',
    included: [], excluded: [], consent_template_code: '', requires_co2: false, requires_lipofilling: false, active: true,
  });
  const [includedStr, setIncludedStr] = useState((initial?.included || []).join('\n'));
  const [excludedStr, setExcludedStr] = useState((initial?.excluded || []).join('\n'));
  useEffect(() => {
    if (open) {
      const i = initial || { code: '', name: '', description: '', price: 0, price_min: '', price_max: '', anesthesia: 'Local + sedación', included: [], excluded: [], consent_template_code: '', requires_co2: false, requires_lipofilling: false, active: true };
      setForm(i);
      setIncludedStr((i.included || []).join('\n'));
      setExcludedStr((i.excluded || []).join('\n'));
      setCaptacion(captacionInicial(initial));
    }
  }, [open, initial]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const sinCaptacion = Object.fromEntries(
      Object.entries(form).filter(([k]) => !CAMPOS_CAPTACION.includes(k)));
    const payload = {
      ...sinCaptacion,
      price: Number(form.price),
      // Phase 25.C — horquilla interna; null si no se rellena
      price_min: form.price_min === '' || form.price_min == null ? null : Number(form.price_min),
      price_max: form.price_max === '' || form.price_max == null ? null : Number(form.price_max),
      included: includedStr.split('\n').filter(Boolean),
      excluded: excludedStr.split('\n').filter(Boolean),
    };
    let id = initial?.id;
    try {
      if (isEdit) { await api.put(`/api/surgeries/${initial.id}`, payload); }
      else { const r = await api.post('/api/surgeries', payload); id = r?.data?.id; }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); return; }
    if (admin && id && captacionCambiada(captacion, initial)) {
      try {
        await api.put(`/api/surgeries/${id}/captacion`, cuerpoCaptacion(captacion));
      } catch (err) {
        // La cirugía sí se guardó: se dice qué no, y la ventana sigue abierta.
        toast.error(`Guardada, pero no el filtro de captación: ${err?.response?.data?.detail || 'error'}`);
        onSaved && onSaved();
        return;
      }
    }
    toast.success(isEdit ? 'Actualizada' : 'Creada');
    onSaved && onSaved(); onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar cirugía' : 'Nueva cirugía'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Código</Label><Input required value={form.code} disabled={isEdit} onChange={(e) => setF('code', e.target.value)} placeholder="codigo-de-la-cirugia" /></div>
          <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setF('name', e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={(e) => setF('description', e.target.value)} /></div>
          <div><Label>Precio público €</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setF('price', e.target.value)} data-testid="surgery-price" /></div>
          <div><Label>Anestesia</Label><Input value={form.anesthesia} onChange={(e) => setF('anesthesia', e.target.value)} /></div>
          {/* Phase 25.C — horquilla interna (solo equipo). No se muestra al paciente. */}
          <div className="md:col-span-2 border-t border-border pt-3 mt-1">
            <Label className="text-[#13231E] font-semibold">Horquilla interna (solo equipo)</Label>
            <p className="text-xs text-muted-foreground mb-2">Estas cifras NO aparecen en presupuestos enviados al paciente. Sirven para orientar al equipo en consulta.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Mínimo €</Label>
                <Input
                  type="number" step="0.01"
                  value={form.price_min ?? ''}
                  onChange={(e) => setF('price_min', e.target.value)}
                  placeholder="Ej. 2500"
                  data-testid="surgery-price-min"
                />
              </div>
              <div>
                <Label className="text-xs">Máximo €</Label>
                <Input
                  type="number" step="0.01"
                  value={form.price_max ?? ''}
                  onChange={(e) => setF('price_max', e.target.value)}
                  placeholder="Ej. 4500"
                  data-testid="surgery-price-max"
                />
              </div>
            </div>
          </div>
          <div className="md:col-span-2"><Label>Incluye (un ítem por línea)</Label><Textarea rows={4} value={includedStr} onChange={(e) => setIncludedStr(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>No incluye (un ítem por línea)</Label><Textarea rows={2} value={excludedStr} onChange={(e) => setExcludedStr(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Código plantilla consentimiento</Label>
            <select value={form.consent_template_code || ''} onChange={(e) => setF('consent_template_code', e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
              <option value="">—</option>
              {templates.map(t => <option key={t.code} value={t.code}>{t.name} ({t.code})</option>)}
            </select>
          </div>
          {admin && (
            <CaptacionCampos estado={captacion} onChange={setCaptacion} nombre={form.name} />
          )}
          <DialogFooter className="md:col-span-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"><Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La tabla de intervenciones con su código (`surgeries_catalog`,
 * `/api/surgeries`), plegada al final de la página y solo para administración
 * (6/10/2026). NO se ha borrado (decisión del director): de ella dependen el
 * consentimiento y los documentos de cada cirugía, la tarifa de Cobros y el KPI
 * de «Captación por origen». Hace todo lo de antes —alta, edición con su
 * plantilla de consentimiento y su captación, borrado—; solo sale de la vista
 * principal, que ahora son los tres desplegables de precios.
 */
function IntervencionesConfig({ admin, abierto, onToggle }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pedido, setPedido] = useState(false);
  const fetchItems = async () => { setLoading(true); try { const [s, t] = await Promise.all([api.get('/api/surgeries'), api.get('/api/consent-templates')]); setItems(Array.isArray(s.data) ? s.data : []); setTemplates(Array.isArray(t.data) ? t.data : []); } finally { setLoading(false); } };
  // Se pide la primera vez que se abre: plegada no hace falta.
  useEffect(() => {
    if (abierto && !pedido) { setPedido(true); fetchItems(); }
  }, [abierto, pedido]);
  const onDelete = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/api/surgeries/${id}`); toast.success('Eliminada'); fetchItems(); };
  return (
    <section className="cjo-card mt-8" id="intervenciones-configuracion" data-testid="catalogo-intervenciones">
      <button type="button" onClick={onToggle} aria-expanded={abierto} data-testid="catalogo-intervenciones-abrir"
        className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <span className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-4 h-4 shrink-0 text-[#2E6E5E] transition-transform ${abierto ? 'rotate-90' : ''}`} />
          <span className="font-semibold text-[#13231E]">Intervenciones y su consentimiento (configuración)</span>
        </span>
        <span className="text-xs text-muted-foreground text-right">Solo administración</span>
      </button>
      {abierto && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <p className="text-xs text-muted-foreground max-w-2xl">
              Cada intervención con su código, su plantilla de consentimiento y su captación. De aquí salen el
              consentimiento y los documentos de cada cirugía, la tarifa de Cobros y el KPI de «Captación por
              origen». {loading ? '' : `${items.length} ${items.length === 1 ? 'intervención' : 'intervenciones'}.`}
            </p>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="catalog-new"><Plus className="w-4 h-4 mr-2" /> Nueva cirugía</Button>
          </div>
          <div className="overflow-hidden border border-border rounded-md">
            {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
              : items.length === 0 ? <EmptyState icon={Scissors} title="Sin intervenciones" description="Añade la primera." />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Código</th><th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Precio</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right" title="Horquilla interna - solo equipo">Horquilla</th><th className="py-3 px-4 font-semibold text-[#13231E]">Anestesia</th><th className="py-3 px-4 font-semibold text-[#13231E]">Plantilla</th><th></th></tr></thead>
                <tbody>{items.map(s => <tr key={s.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="catalog-row">
                  <td className="py-3 px-4 text-xs font-mono">{s.code}</td>
                  <td className="py-3 px-4 font-medium">
                    {s.name}
                    {s.captacion && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.1)] text-[#2E6E5E]"
                        title="Sale en el filtro de «Captación por origen»" data-testid="catalog-captacion">
                        Captación
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right tabular-nums font-semibold text-[#13231E]">{formatEUR(s.price)}</td>
                  <td className="py-3 px-4 text-right text-xs text-muted-foreground tabular-nums">
                    {(s.price_min != null || s.price_max != null) ? (
                      <span title="Solo equipo">
                        {s.price_min != null ? formatEUR(s.price_min) : '?'} – {s.price_max != null ? formatEUR(s.price_max) : '?'}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-3 px-4 text-xs">{s.anesthesia}</td>
                  <td className="py-3 px-4 text-xs">{s.consent_template_code || '—'}</td>
                  <td className="py-3 px-4 text-right"><div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid="catalog-edit"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div></td>
                </tr>)}</tbody>
              </table></div>}
          </div>
        </div>
      )}
      <SurgeryDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} templates={templates} admin={admin} />
    </section>
  );
}

export default function CatalogPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const gestion = isGestion(user);
  // La tabla de intervenciones del final se abre solo con su propia cabecera
  // (27/10/2026, administración: se quitó el bloque «Ya en el sistema:
  // intervenciones con precio público» de CIRUGÍAS y, con él, el enlace que
  // la abría desde allí).
  const [intervencionesAbiertas, setIntervencionesAbiertas] = useState(false);
  return (
    <div>
      {/* Renombrado el 6/10/2026 (administración): antes «Catálogo de
          cirugías». Arriba, los desplegables de precios (desde el 23/10/2026
          también «Alquiler quirófano externos»); la tabla de intervenciones
          con código, plegada al final y solo para admin. */}
      <PageHeader title="Catálogo de cirugías, tratamientos y otros costes"
        subtitle="Precios de los tratamientos y de las cirugías, las tarifas de los hospitales y el alquiler del quirófano a externos." />
      <PreciosCatalogo admin={admin} gestion={gestion} />
      {/* Listas pequeñas de la clínica que antes venían en el código (V2). */}
      <ProductosMapaFacialEditor admin={admin} />
      <FiltroRegistroQxEditor admin={admin} />
      <LugaresQuirofanoEditor admin={admin} />
      <ClinicasOtroCirujanoEditor admin={admin} />
      <ClinicasQuirofanoEditor admin={admin} />
      {/* La marca de los documentos de cada médico del Catálogo QX (20/10/2026). */}
      <MarcaDocumentosMedicos admin={admin} />
      {admin && (
        <IntervencionesConfig admin={admin} abierto={intervencionesAbiertas}
          onToggle={() => setIntervencionesAbiertas((a) => !a)} />
      )}
    </div>
  );
}
